<template>
  <div
    ref="root"
    v-click-outside="onClickOutside"
    class="checklist-table-column-title pl-7"
    :class="{
      'with-border': isTableHorizontallyScrolled,
      'is-editing': editTitleIsActive,
    }"
  >
    <div class="checklist-table-column-title-inner">
      <div :style="{ 'padding-left': taskTitleDepthPadding }" class="position-display">
        {{ task.positionDisplay }}
      </div>

      <template v-if="editTitleIsActive && editableTask">
        <ChecklistTableTitleInput
          class="mt-n1"
          v-model.trim="editableTask.title"
          data-locator="title-input"
          @enter="prepareEditTask"
          @update:focused="onTitleInputFocusChanged"
          :type="task.type"
        />
      </template>
      <template v-else>
        <p
          class="ChecklistTableTaskTitle-title--inactive mb-0"
          @click.stop="enableTitleEdition"
          data-locator="task-title"
        >
          <template v-if="!computedTitle">
            <div class="text-grey-300">
              {{ t('defaultTitle') }}
            </div>
          </template>
          <template v-else>
            {{ computedTitle }}
          </template>
        </p>
        <template v-if="canOpenChecklistPane">
          <span class="sidepane-button">
            <app-button
              type="icon"
              size="small"
              class="px-2 d-flex align-center text-grey-800"
              data-locator="sidepane-button"
              @click="onOpenChecklistPane"
            >
              <app-icon-ds
                icon="sidebar-flip"
                size="xs"
                weight="far"
              />
              {{ t('open') }}
            </app-button>
          </span>
        </template>
      </template>
    </div>
    <div :style="{ 'padding-left': taskTitleDepthPadding }">
      <template v-if="isChecklistDetailed">
        <ChecklistTableTaskDescription 
          :item="task"
          :parent="parent"
        />
      </template>
      <div class="d-flex align-center mb-1 column-gap-2">
        <ChecklistTableTaskShortcutRights
          :item="task"
          :parent="parent"
        />
        <ChecklistTableTaskShortcutDocument
          :item="task"
          :parent="parent"
        />
        <ChecklistTableTaskShortcutDescription 
          :item="task"
          :parent="parent"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject, PropType } from 'vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import { scrollToAddedItem } from '@/common/utils/checklist'
import { toast } from '@/common/utils/toast'
import { useTranslation } from '@/common/utils/translation'
import { ChecklistSection, ChecklistTask } from '@/project/checklist/types'
import {
  ADD_TASK_TO_SELECTION,
  EDIT_TASK,
  GET_TASK_DETAILS,
  REMOVE_TASK_FROM_SELECTION,
  OPEN_CHECKLIST_PANE,
} from '@/store/modules/checklist/action_types'
import {
  ADD_CHECKLIST_ITEM_BEING_EDITED,
  REMOVE_CHECKLIST_ITEM_BEING_EDITED,
  SET_CHECKLIST_TITLE_INPUT_INVALID,
} from '@/store/modules/checklist/mutation_types'

import ChecklistTableTaskDescription from './ChecklistTableTaskDescription.vue'
import ChecklistTableTaskShortcutDescription from './ChecklistTableTaskShortcutDescription.vue'
import ChecklistTableTaskShortcutDocument from './ChecklistTableTaskShortcutDocument.vue'
import ChecklistTableTaskShortcutRights from './ChecklistTableTaskShortcutRights.vue'
import ChecklistTableTitleInput from './ChecklistTableTitleInput.vue'

type DataType = {
  editTitleIsActive: boolean;
  editableTask: {
    title: string
  } | null;
  lastSavedTitle: string | null;
}

export default defineComponent({
  name: 'ChecklistTableTaskTitle',
  components: {
    ChecklistTableTaskDescription,
    ChecklistTableTaskShortcutDescription,
    ChecklistTableTaskShortcutDocument,
    ChecklistTableTaskShortcutRights,
    ChecklistTableTitleInput,
  },
  props: {
    isHovered: {
      type: Boolean,
    },
    task: {
      type: Object as PropType<ChecklistTask>,
      required: true,
    },
    parent: {
      type: Object as PropType<ChecklistSection>,
      required: true,
    },
  },
  inject: ['isTableHorizontallyScrolled'],
  setup () {
    const isTableHorizontallyScrolled = inject<boolean>('isTableHorizontallyScrolled')
    const { t } = useTranslation({ keyPrefix: 'project.checklist.table.ChecklistTableTaskTitle' })

    return {
      isTableHorizontallyScrolled,
      t,
    }
  },
  data (): DataType {
    return {
      editTitleIsActive: false,
      editableTask: null,
      lastSavedTitle: null,
    }
  },
  computed: {
    ...mapState('checklist', ['checklistDetailsPending','editTaskPending', 'lastItemAdded', 'checklistTitleInputInvalid']),
    ...mapGetters('checklist', ['selectedTasks']),
    ...mapGetters('room', ['isCurrentUserPm', 'roomMnemo']),
    ...mapGetters('user', ['isChecklistDetailed']),
    canOpenChecklistPane () {
      return this.editTaskPending !== this.task.id && !this.checklistDetailsPending && this.isHovered
    },
    isLastTaskAdded () {
      if (this.lastItemAdded) {
        return this.lastItemAdded.id === this.task.id && this.lastItemAdded.type === this.task.type
      }
      return false
    },
    isTaskSelected () {
      return this.selectedTasks.some((task: ChecklistTask) => task.id === this.task.id)
    },
    computedTitle () {
      if (this.lastSavedTitle) {
        return this.lastSavedTitle
      }
      return this.task.title || ''
    },
    taskTitleDepthPadding () {
      return this.task.depth > 0 ? `${20 * this.task.depth}px` : 0
    },
  },
  watch: {
    isLastTaskAdded (value) {
      if (value) {
        this.onLastTaskAdded()
      }
    },
    checklistDetailsPending (value) {
      if (!value && this.isLastTaskAdded) {
        this.onLastTaskAdded()
      }
    },
    'task.title' (value) {
      if (value === this.lastSavedTitle) {
        this.lastSavedTitle = null
      }
    },
  },
  created () {
    this.editableTask = {
      title: this.task.title,
    }
    this.SET_CHECKLIST_TITLE_INPUT_INVALID(false)
  },
  methods: {
    ...mapActions('checklist', [
      ADD_TASK_TO_SELECTION,
      EDIT_TASK,
      GET_TASK_DETAILS,
      OPEN_CHECKLIST_PANE,
      REMOVE_TASK_FROM_SELECTION,
    ]),
    ...mapMutations('checklist', [ADD_CHECKLIST_ITEM_BEING_EDITED, REMOVE_CHECKLIST_ITEM_BEING_EDITED, SET_CHECKLIST_TITLE_INPUT_INVALID]),
    onLastTaskAdded () {
      const hasValidTitle = this.task.title && this.task.title.trim() !== ''
      
      if (this.checklistDetailsPending || hasValidTitle) {
        return
      }

      const newTaskElement = this.$refs['root'] as HTMLElement
      if (newTaskElement) {
        scrollToAddedItem(newTaskElement)
      }
      this.enableTitleEdition()
    },
    onTitleInputFocusChanged (hasInputFocus: boolean) {
      const item = { type: 'task', id: this.task.id }
      if (hasInputFocus) {
        this.ADD_CHECKLIST_ITEM_BEING_EDITED(item)
      } else {
        this.REMOVE_CHECKLIST_ITEM_BEING_EDITED(item)
      }
    },
    enableTitleEdition () {
      if (!this.isCurrentUserPm || this.checklistTitleInputInvalid) {
       return
      }

      this.editableTask = {
        title: this.task.title,
      }
      
      this.editTitleIsActive = true
    },
    async prepareEditTask () {
      if (!this.editableTask?.title) {
        toast.error(this.t('invalidTitleError'))
        return
      }
      if (!this.editableTask) {
        return
      }

      if (this.task.title === this.editableTask.title) {
        this.editTitleIsActive = false
      } else {
        const data: { title?: string } = {}
        
        if (this.task.title !== this.editableTask.title) {
          data.title = this.editableTask.title
          this.lastSavedTitle = this.editableTask.title
        }

        try {
          await this.EDIT_TASK({
            mnemo: this.roomMnemo,
            taskId: this.task.id,
            data,
          })

          this.GET_TASK_DETAILS({
            mnemo: this.roomMnemo,
            taskId: this.task.id,
          })

          this.editTitleIsActive = false
        } catch (e) {
          console.error(e)

          this.lastSavedTitle = null
          if (this.task.title === this.editableTask.title) { // No toast for renamming title
            toast.error(this.t('editTaskError'))
          }
        }
      }
    },
    onClickOutside ($event: Event) {
      if (this.editTitleIsActive) {
        const isClickOnRenameButton = ($event.target as HTMLElement)?.classList?.contains('rename-button')
        if (!isClickOnRenameButton) this.prepareEditTask()
      }
    },
    onSelectedTaskClick () {
      if (this.isTaskSelected) {
        this.REMOVE_TASK_FROM_SELECTION(this.task)
      } else {
        this.ADD_TASK_TO_SELECTION(this.task)
      }
    },
    onOpenChecklistPane() {
      this.OPEN_CHECKLIST_PANE({
        isOpen: true,
        parent: this.parent,
        tab: 'details',
        tasks: [this.task],
      })
    },
  },
})
</script>

<style scoped lang="scss">
:deep(.ChecklistTableTaskTitle-title--inactive) {
  cursor: pointer;
}

.ChecklistTableTaskTitle-mobileRow {
  border-bottom: 2px solid #eee;
}
.ChecklistTableTaskTitle-mobileRowGrid {
  display: grid;
  grid-template-columns: 1fr 2fr;
}

.checklist-table-column-title {
  background-color: inherit;
  padding: 4px 8px;
  align-items: start;
  
  .checklist-table-task:not(.drag-in-progress):not(.checklist-table-task--opened):not(:hover):not(.selected) &:not(.is-editing) {
    background-color: white;
  }
}

.checklist-table-column-title-inner {
  align-items: start;
  background-color: inherit;
  column-gap: 8px;
  display: grid;
  font-weight: 500;
  grid-template-columns: auto 1fr auto;
  min-height: 32px;

  p,
  .position-display {
    padding: 8px 0;
  }
}

.sidepane-button {
  background-color: rgb(var(--v-theme-white)) !important;
  border:1px solid rgb(var(--v-theme-grey-100));
  border-radius: 6px;
  position: absolute;
  right: 4px;
  top: 6px;

  button:hover {
    background-color: inherit !important;
    box-shadow: 0 2px 20px 0 rgba(68, 74, 80, 0.08);
  }
}
</style>
